import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { PropertyCard } from "./PropertyCard";
import { PropertyCardList } from "./PropertyCardList";
import { publicSupabase } from "@/lib/public-supabase";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdvancedSearchFilters, BasicSearchFilters } from "@/types/search";
import PropertyMap from "@/components/maps/PropertyMap";
import { Grid, List, Map, MapPin, Search, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";
import { getPropertyUrl } from "@/utils/slugify";
import { sanitizeSearchQuery } from "@/utils/validation";
import { fetchPublishedPropertiesWithoutPreflight } from "@/lib/public-properties-fallback";
import { type PossessionStage, filterByPossessionStage } from "@/components/public/PossessionStageFilter";
import { normalizeListingType } from "@/components/shared/ListingTypeBadge";

import { filterProperties } from "@/utils/propertySearch";

interface Property {
  id: string;
  title: string;
  slug?: string | null;
  project_name?: string | null;
  locality: string | null;
  city: string | null;
  address?: string | null;
  price: number | null;
  price_min?: number | null;
  price_max?: number | null;
  price_on_request?: boolean | null;
  property_type: string;
  area_sqft?: number | null;
  area_min?: number | null;
  area_max?: number | null;
  bedrooms?: number | null;
  bedrooms_options?: number[] | null;
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  is_featured: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
  developer_name?: string | null;
  possession_date?: string | null;
  possession_stage?: string | null;
  listing_type?: string | null;
  amenities?: string[] | null;
}

export type ListingTypeFilter = 'all' | 'sale' | 'rent';

interface PropertyGridProps {
  searchFilters?: AdvancedSearchFilters | BasicSearchFilters;
  showAdvancedSearch?: boolean;
  viewMode?: 'grid' | 'list' | 'map';
  onViewModeChange?: (mode: 'grid' | 'list' | 'map') => void;
  possessionStage?: PossessionStage;
  sortBy?: string;
  onSortChange?: (value: string) => void;
  listingType?: ListingTypeFilter;
  onListingTypeChange?: (value: ListingTypeFilter) => void;
}

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low → High" },
  { value: "price-high", label: "Price: High → Low" },
  { value: "area-large", label: "Area: Large → Small" },
  { value: "area-small", label: "Area: Small → Large" },
];

const PROPERTY_TYPE_ALIASES: Record<string, string[]> = {
  commercial: ["commercial", "office", "shop", "warehouse", "retail", "industrial"],
  plot: ["plot", "land"],
  land: ["land", "plot"],
};

const resolvePropertyTypeFilters = (propertyType?: string): string[] => {
  const normalized = propertyType?.trim().toLowerCase();
  if (!normalized) return [];
  return PROPERTY_TYPE_ALIASES[normalized] ?? [normalized];
};

const GRID_CACHE_KEY = "mnre-properties-grid-cache";

const readGridCache = (): Property[] => {
  try {
    const raw = localStorage.getItem(GRID_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGridCache = (items: Property[]) => {
  try {
    localStorage.setItem(GRID_CACHE_KEY, JSON.stringify(items));
  } catch {
    // Ignore cache write failures in private mode/quota limits
  }
};

export const PropertyGrid = ({
  searchFilters,
  showAdvancedSearch = false,
  viewMode = 'grid',
  onViewModeChange,
  possessionStage = 'all',
  sortBy,
  onSortChange,
  listingType,
  onListingTypeChange
}: PropertyGridProps) => {
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalListingType, setInternalListingType] = useState<ListingTypeFilter>('all');
  const listingTypeFilter = listingType ?? internalListingType;
  const setListingTypeFilter = (value: ListingTypeFilter) => {
    if (onListingTypeChange) onListingTypeChange(value);
    else setInternalListingType(value);
  };
  const navigate = useNavigate();
  const properties = useMemo(() => {
    const byStage = filterByPossessionStage(allProperties, possessionStage);
    if (listingTypeFilter === 'all') return byStage;
    return byStage.filter(p => normalizeListingType(p.listing_type) === listingTypeFilter);
  }, [allProperties, possessionStage, listingTypeFilter]);


  useEffect(() => {
    fetchProperties();
  }, [searchFilters]);

  const applySearchFilters = (items: Property[]): Property[] => {
    return filterProperties(items, searchFilters);
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primary: direct REST fetch (bypasses SDK completely)
      const results = await fetchPublishedPropertiesWithoutPreflight();
      const typedResults = results as Property[];
      writeGridCache(typedResults);
      setAllProperties(applySearchFilters(typedResults));
    } catch (err) {
      console.error("Direct REST fetch failed, trying SDK:", err);
      try {
        // Fallback: try SDK
        const { data, error } = await publicSupabase
          .from("properties")
          .select(`id, title, slug, project_name, locality, city, address, price, price_min, price_max, price_on_request, property_type, listing_type, area_sqft, area_min, area_max, bedrooms, bedrooms_options, bathrooms, images, description, is_featured, latitude, longitude, developer_name, created_at, possession_date, possession_stage, status`)
          .eq("is_published", true)
          .eq("status", "active")
          .order("is_featured", { ascending: false })
          .order("created_at", { ascending: false });
        if (error) throw error;
        const typedResults = ((data || []) as any[]).filter(p => !p.deleted_at) as Property[];
        writeGridCache(typedResults);
        setAllProperties(applySearchFilters(typedResults));
      } catch (sdkErr) {
        console.error("SDK fetch also failed:", sdkErr);
        const cached = readGridCache();
        if (cached.length > 0) {
          setAllProperties(applySearchFilters(cached.filter((p: any) => (p.status || "active") === "active")));
          setError(null);
        } else {
          setError("Failed to load properties. Please try again later.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Re-sort when sortBy changes
  useEffect(() => {
    if (sortBy && properties.length > 0) {
      let sorted = [...allProperties];
      if (sortBy === 'price-low') sorted.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
      else if (sortBy === 'price-high') sorted.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
      else if (sortBy === 'area-large') sorted.sort((a, b) => (b.area_sqft ?? 0) - (a.area_sqft ?? 0));
      else if (sortBy === 'area-small') sorted.sort((a, b) => (a.area_sqft ?? 0) - (b.area_sqft ?? 0));
      setAllProperties(sorted);
    }
  }, [sortBy]);

  const handlePropertyClick = (property: Property) => navigate(getPropertyUrl(property as any));
  const propertiesWithCoordinates = properties.filter(p => p.latitude && p.longitude);

  // Sticky toolbar
  const Toolbar = () => (
    <div className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b py-3 -mx-4 px-4 mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center bg-muted p-0.5 rounded-lg">
            {(['all', 'sale', 'rent'] as const).map(t => (
              <Button
                key={t}
                variant={listingTypeFilter === t ? 'default' : 'ghost'}
                size="sm"
                className="h-8 px-3 text-xs capitalize"
                onClick={() => setListingTypeFilter(t)}
              >
                {t === 'all' ? 'All' : t === 'sale' ? 'For Sale' : 'For Rent'}
              </Button>
            ))}
          </div>
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{properties.length}</span> properties found
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick sort */}
          <Select value={sortBy || ''} onValueChange={(v) => onSortChange?.(v)}>
            <SelectTrigger className="w-[180px] h-9 text-sm">
              <SlidersHorizontal className="h-3 w-3 mr-1" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View toggle */}
          {onViewModeChange && (
            <div className="flex items-center bg-muted p-0.5 rounded-lg">
              <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" className="h-8 px-2.5" onClick={() => onViewModeChange('grid')}>
                <Grid className="h-4 w-4" />
              </Button>
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className="h-8 px-2.5" onClick={() => onViewModeChange('list')}>
                <List className="h-4 w-4" />
              </Button>
              <Button variant={viewMode === 'map' ? 'default' : 'ghost'} size="sm" className="h-8 px-2.5" onClick={() => onViewModeChange('map')}>
                <Map className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Alert variant="destructive" className="max-w-md mx-auto">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => fetchProperties()} variant="outline" className="mt-4">Try Again</Button>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
          <Search className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Properties Found</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Try adjusting your search filters, broadening your location, or check back later for new listings.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/properties')}>Clear Filters</Button>
          <Button onClick={() => navigate('/contact')}>Contact Us</Button>
        </div>
      </div>
    );
  }

  if (viewMode === 'map') {
    return (
      <div className="space-y-0">
        <Toolbar />
        {propertiesWithCoordinates.length > 0 ? (
          <PropertyMap
            properties={propertiesWithCoordinates.map(p => ({
              id: p.id, title: p.title,
              location: `${p.locality || ''}, ${p.city || ''}`.replace(/^, |, $/g, ''),
              latitude: p.latitude!, longitude: p.longitude!,
              price: p.price ?? 0, property_type: p.property_type
            }))}
            height="600px" showControls onPropertyClick={(p) => handlePropertyClick(p as any)}
          />
        ) : (
          <Card className="p-8 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Location Data Available</h3>
            <p className="text-muted-foreground">Switch to grid or list view to see all properties.</p>
          </Card>
        )}
        <div className="text-sm text-muted-foreground text-center mt-4">
          Showing {propertiesWithCoordinates.length} of {properties.length} on map
        </div>
      </div>
    );
  }

  const toCard = (p: Property) => ({ ...p, location: p.locality, address: p.address || null, city: p.city || '', is_featured: p.is_featured || false } as any);

  return (
    <div className="space-y-0">
      <Toolbar />
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {properties.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, duration: 0.3 }}>
              <PropertyCardList property={toCard(p)} />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, duration: 0.3 }}>
              <PropertyCard property={toCard(p)} />
            </motion.div>
          ))}
        </div>
      )}
      {properties.length > 0 && (
        <p className="text-xs text-muted-foreground leading-relaxed mt-6 px-1">
          * Images shown are for representation purposes only and may not reflect the actual property. Prices mentioned are subject to change without prior notice.
        </p>
      )}
    </div>
  );
};
